import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Predecessor, TaskItem, TaskList } from '../../../types/tasks';
import { useTasks } from '../../../context/TasksContext';
import { addDays, daysBetween } from '../../../utils/taskDates';
import { floats, hasConflict, isCritical, isScheduled } from '../../../utils/projectPlan';
import { PROJECT_MAX_DEPTH, childrenOf } from '../../../utils/taskTree';
import TaskIcon from '../../tasks/TaskIcon';
import { Folding } from '../useCollapsed';
import FoldButtons from '../FoldButtons';
import GanttRow, { BarDrag, GanttRowData } from './GanttRow';
import { GanttZoom, PX_PER_DAY, ganttRange, ganttTicks } from './scale';

const ROW = 36;
const DRAG_THRESHOLD = 3;

function readPref<T extends string>(key: string, allowed: T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}
function writePref(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* per-viewer convenience only */
  }
}

type DragMode = BarDrag | 'range';

interface Drag {
  id: number;
  mode: DragMode;
  pointerId: number;
  startX: number;
  dx: number;
  moved: boolean;
  start: string;
  finish: string;
  /** link mode: where the pointer is, in body coordinates, and the bar under it. */
  at: { x: number; y: number } | null;
  overId: number | null;
  /** range mode: the row's timeline, to read days off as the pointer moves. */
  track: HTMLElement | null;
}

interface Dates {
  start: string;
  finish: string;
}

/**
 * A project as a Gantt chart: the outline on the left, the plan on the right.
 * Bars come from the scheduler (plan_start / plan_finish). Dragging a bar
 * fixes its start; dragging its end sets how long it takes; dragging from its
 * round handle onto another bar makes that one wait for it. Red = critical
 * path or late; a red tick is a deadline (due date); a tinted band is an event.
 */
const GanttView: React.FC<{ project: TaskList; items: TaskItem[]; readOnly: boolean; focusItemId: number | null; fold: Folding }> = ({
  project,
  items,
  readOnly,
  focusItemId,
  fold,
}) => {
  const { today, updateItem, createItem, updateList } = useTasks();
  const [zoom, setZoomState] = useState<GanttZoom>(() => readPref('project.ganttZoom', ['day', 'week', 'month'], 'week'));
  const [scope, setScopeState] = useState<'all' | 'scheduled'>(() => readPref('project.ganttScope', ['all', 'scheduled'], 'all'));
  const setZoom = (z: GanttZoom) => {
    setZoomState(z);
    writePref('project.ganttZoom', z);
  };
  const setScope = (s: 'all' | 'scheduled') => {
    setScopeState(s);
    writePref('project.ganttScope', s);
  };
  const { collapsed, toggle, expandTo } = fold;
  const [selected, setSelected] = useState<number | null>(null);
  const [flashId, setFlashId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const narrow = typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 640px)').matches;
  const LEFT = narrow ? 170 : 300;

  const px = PX_PER_DAY[zoom];
  const { from, to } = useMemo(() => ganttRange(items, today), [items, today]);
  const width = (daysBetween(from, to) + 1) * px;
  const x = (day: string) => daysBetween(from, day) * px;
  const ticks = useMemo(() => ganttTicks(from, to, zoom), [from, to, zoom]);

  // ── Rows: the outline, folded as on the map ────────────────────────────────
  const kids = useMemo(() => childrenOf(items), [items]);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const withPlan = useMemo(() => {
    const out = new Set<number>();
    const walk = (i: TaskItem): boolean => {
      let any = isScheduled(i);
      for (const c of kids.get(i.id) || []) any = walk(c) || any;
      if (any) out.add(i.id);
      return any;
    };
    (kids.get(null) || []).forEach(walk);
    return out;
  }, [kids]);
  const rows = useMemo(() => {
    const out: GanttRowData[] = [];
    const walk = (item: TaskItem, depth: number, branch: number) => {
      if (scope === 'scheduled' && !withPlan.has(item.id)) return;
      const children = kids.get(item.id) || [];
      out.push({ item, depth, hasChildren: children.length > 0, branch });
      if (!collapsed.has(item.id)) children.forEach((c) => walk(c, depth + 1, branch));
    };
    (kids.get(null) || []).forEach((m, i) => walk(m, 0, i));
    return out;
  }, [kids, collapsed, scope, withPlan]);
  const rowIndex = useMemo(() => new Map(rows.map((r, i) => [r.item.id, i])), [rows]);

  // ── Dates as drawn: a drag in progress, then the dropped dates until the
  // server's re-plan arrives, then the plan. ────────────────────────────────
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  dragRef.current = drag;
  const [pending, setPending] = useState<Map<number, Dates & { rev: number }>>(new Map());

  const datesOf = (item: TaskItem): Dates | null => {
    if (drag && drag.id === item.id && (drag.mode === 'move' || drag.mode === 'resize') && drag.moved) {
      const delta = Math.round(drag.dx / px);
      if (drag.mode === 'move') return { start: addDays(drag.start, delta), finish: addDays(drag.finish, delta) };
      const finish = addDays(drag.finish, delta);
      return { start: drag.start, finish: finish < drag.start ? drag.start : finish };
    }
    const p = pending.get(item.id);
    if (p && p.rev === item.rev) return p;
    return isScheduled(item) ? { start: item.plan_start!, finish: item.plan_finish! } : null;
  };

  // ── Dragging bars ──────────────────────────────────────────────────────────
  const onBarPointerDown = (item: TaskItem) => (e: React.PointerEvent, mode: BarDrag) => {
    e.stopPropagation();
    setSelected(item.id);
    if (readOnly || (e.button !== 0 && e.pointerType === 'mouse')) return;
    // A floating event starts when what it waits for is done — only its length can be dragged.
    if (mode === 'move' && floats(item)) return;
    const d = datesOf(item);
    if (!d) return;
    e.preventDefault();
    setDrag({ id: item.id, mode, pointerId: e.pointerId, startX: e.clientX, dx: 0, moved: false, start: d.start, finish: d.finish, at: null, overId: null, track: null });
  };

  /** The day under the pointer on a row's timeline. */
  const dayAt = (track: HTMLElement, clientX: number) =>
    addDays(from, Math.floor((clientX - track.getBoundingClientRect().left) / px));

  // Press on the empty part of a row and drag across the days it should take.
  // Not on touch, where the same gesture scrolls the chart.
  const onTrackPointerDown = (item: TaskItem) => (e: React.PointerEvent) => {
    if (e.target !== e.currentTarget || e.pointerType === 'touch' || (e.button !== 0 && e.pointerType === 'mouse')) return;
    const track = e.currentTarget as HTMLElement;
    const day = dayAt(track, e.clientX);
    e.preventDefault();
    setSelected(item.id);
    setDrag({ id: item.id, mode: 'range', pointerId: e.pointerId, startX: e.clientX, dx: 0, moved: false, start: day, finish: day, at: null, overId: null, track });
  };

  /** The dragged-out range, earliest day first. */
  const rangeOf = (d: Drag) => (d.start <= d.finish ? { start: d.start, finish: d.finish } : { start: d.finish, finish: d.start });

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      const dx = e.clientX - d.startX;
      const moved = d.moved || Math.abs(dx) > DRAG_THRESHOLD;
      let at = d.at;
      let overId = d.overId;
      if (d.mode === 'link' && bodyRef.current) {
        const rect = bodyRef.current.getBoundingClientRect();
        at = { x: e.clientX - rect.left - LEFT, y: e.clientY - rect.top };
        const el = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)?.closest('[data-gantt-id]') as HTMLElement | null;
        const id = el ? Number(el.dataset.ganttId) : null;
        overId = id && id !== d.id ? id : null;
      }
      const finish = d.mode === 'range' && d.track ? dayAt(d.track, e.clientX) : d.finish;
      setDrag({ ...d, dx, moved, at, overId, finish });
    };
    const onUp = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      setDrag(null);
      if (d.moved || d.mode === 'link') drop(d);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.id, drag?.pointerId]);

  const drop = (d: Drag) => {
    const item = byId.get(d.id);
    if (!item) return;
    if (d.mode === 'range') {
      applyRange(item, rangeOf(d));
      return;
    }
    if (d.mode === 'link') {
      const target = d.overId ? byId.get(d.overId) : undefined;
      if (!target || target.id < 0) return;
      const preds: Predecessor[] = target.predecessors || [];
      if (preds.some((p) => p.id === item.id)) return;
      updateItem(target.id, { predecessors: [...preds, { id: item.id, type: 'FS', lag: 0 }] });
      return;
    }
    const delta = Math.round(d.dx / px);
    if (!delta) return;
    const kind = item.node_kind || 'task';
    const patch: Partial<TaskItem> = {};
    let next: Dates;
    if (d.mode === 'move') {
      next = { start: addDays(d.start, delta), finish: addDays(d.finish, delta) };
      if (kind === 'milestone') patch.due_date = next.finish;
      else {
        // Dragging fixes where it starts. A start-and-end pair (an event, or a
        // task with both dates set) moves as one.
        patch.start_date = next.start;
        if (item.due_date && (kind === 'event' || item.start_date)) patch.due_date = addDays(item.due_date, delta);
        else if (kind === 'task' && !item.duration_days) patch.duration_days = daysBetween(d.start, d.finish) + 1;
      }
    } else {
      const finish = addDays(d.finish, delta);
      next = { start: d.start, finish: finish < d.start ? d.start : finish };
      if (floats(item)) {
        patch.duration_days = daysBetween(next.start, next.finish) + 1;
      } else if (kind === 'event' || (item.start_date && item.due_date)) {
        patch.due_date = next.finish;
        if (!item.start_date) patch.start_date = d.start;
      } else {
        patch.duration_days = daysBetween(next.start, next.finish) + 1;
      }
    }
    setPending((prev) => new Map(prev).set(item.id, { ...next, rev: item.rev }));
    updateItem(item.id, patch);
  };

  /**
   * Dates dragged out on a row: a task gets a fixed start and that many days
   * (or both dates, if it already had both); an event its start and end; a
   * milestone the day the drag ended on. An event that floats only takes the
   * length — it still starts when what it waits for is done.
   */
  const applyRange = (item: TaskItem, r: Dates) => {
    const kind = item.node_kind || 'task';
    const days = daysBetween(r.start, r.finish) + 1;
    let patch: Partial<TaskItem>;
    let shown: Dates = r;
    if (kind === 'milestone') {
      patch = { due_date: r.finish };
      shown = { start: r.finish, finish: r.finish };
    } else if (floats(item)) {
      patch = { duration_days: days };
      if (item.plan_start) shown = { start: item.plan_start, finish: addDays(item.plan_start, days - 1) };
    } else if (kind === 'event' || (item.start_date && item.due_date)) {
      patch = { start_date: r.start, due_date: r.finish };
    } else {
      patch = { start_date: r.start, duration_days: days };
    }
    setPending((prev) => new Map(prev).set(item.id, { ...shown, rev: item.rev }));
    updateItem(item.id, patch);
  };

  const scheduleAt = (item: TaskItem, day: string) => {
    if (item.node_kind === 'milestone') updateItem(item.id, { due_date: day });
    else updateItem(item.id, { start_date: day, ...(item.duration_days ? {} : { duration_days: 1 }) });
  };

  const addChild = (item: TaskItem) => {
    if (item.depth >= PROJECT_MAX_DEPTH) return;
    if (collapsed.has(item.id)) toggle(item);
    const siblings = kids.get(item.id) || [];
    const last = siblings[siblings.length - 1];
    createItem({ list_id: project.id, parent_id: item.id, text: 'New topic', sort_order: last ? last.sort_order + 1 : 1 });
  };

  // ── Where to look ──────────────────────────────────────────────────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = Math.max(0, x(today) - 160);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, from]);

  const handledFocus = useRef<number | null>(null);
  useEffect(() => {
    if (!focusItemId || handledFocus.current === focusItemId) return;
    const item = byId.get(focusItemId);
    if (!item) return;
    expandTo(item);
    const idx = rowIndex.get(focusItemId);
    if (idx === undefined) return;
    handledFocus.current = focusItemId;
    setSelected(focusItemId);
    setFlashId(focusItemId);
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = Math.max(0, idx * ROW - el.clientHeight / 3);
      const d = datesOf(item);
      if (d) el.scrollLeft = Math.max(0, x(d.start) - 160);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusItemId, byId, rowIndex, expandTo]);
  useEffect(() => {
    if (!flashId) return;
    const t = window.setTimeout(() => setFlashId(null), 2600);
    return () => window.clearTimeout(t);
  }, [flashId]);

  // ── Arrows ─────────────────────────────────────────────────────────────────
  const ends = (item: TaskItem) => {
    const idx = rowIndex.get(item.id);
    const d = datesOf(item);
    if (idx === undefined || !d) return null;
    const y = idx * ROW + ROW / 2;
    if ((item.node_kind === 'milestone') && !(kids.get(item.id) || []).length) {
      const c = x(d.finish) + px / 2;
      return { s: c - 7, e: c + 7, y };
    }
    return { s: x(d.start), e: x(d.finish) + px, y };
  };
  const arrows: { key: string; d: string; critical: boolean; conflict: boolean }[] = [];
  for (const r of rows) {
    for (const dep of r.item.predecessors || []) {
      const pred = byId.get(dep.id);
      if (!pred) continue;
      const a = ends(pred);
      const b = ends(r.item);
      if (!a || !b) continue;
      const fromEnd = dep.type === 'FS' || dep.type === 'FF';
      const toStart = dep.type === 'FS' || dep.type === 'SS';
      const x1 = fromEnd ? a.e : a.s;
      const x2 = toStart ? b.s : b.e;
      const out = x1 + (fromEnd ? 8 : -8);
      const into = x2 + (toStart ? -8 : 8);
      const straight = fromEnd && toStart && out <= into;
      const mid = straight ? b.y : a.y + (b.y > a.y ? ROW / 2 : -ROW / 2);
      arrows.push({
        key: `${pred.id}-${r.item.id}`,
        d: `M${x1} ${a.y} H${out} V${mid} H${into} V${b.y} H${x2}`,
        critical: isCritical(pred) && isCritical(r.item),
        // The link a pinned start breaks: it arrives before it leaves.
        conflict: hasConflict(r.item) && x2 < x1 + (dep.lag || 0) * px,
      });
    }
  }
  const dragLine = (() => {
    if (!drag || drag.mode !== 'link' || !drag.at) return null;
    const item = byId.get(drag.id);
    const a = item ? ends(item) : null;
    return a ? `M${a.e} ${a.y} L${drag.at.x} ${drag.at.y}` : null;
  })();

  const events = items.filter((i) => i.node_kind === 'event' && isScheduled(i) && !(kids.get(i.id) || []).length);
  const nothingPlanned = !items.some(isScheduled);
  const grid =
    zoom === 'month'
      ? undefined
      : [
          `repeating-linear-gradient(to right, transparent 0, transparent ${5 * px}px, var(--tp-weekend) ${5 * px}px, var(--tp-weekend) ${7 * px}px)`,
          `repeating-linear-gradient(to right, var(--tp-gridline) 0, var(--tp-gridline) 1px, transparent 1px, transparent ${(zoom === 'day' ? 1 : 7) * px}px)`,
        ].join(', ');

  return (
    <div className="tp-gantt-wrap">
      <div className="tp-gantt-tools">
        <div className="tk-segmented tk-segmented-sm" role="group" aria-label="Zoom">
          {(['day', 'week', 'month'] as GanttZoom[]).map((z) => (
            <button key={z} type="button" className={zoom === z ? 'is-active' : ''} onClick={() => setZoom(z)}>
              {z === 'day' ? 'Days' : z === 'week' ? 'Weeks' : 'Months'}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="tk-btn tk-btn-ghost tk-btn-sm"
          onClick={() => scrollRef.current && (scrollRef.current.scrollLeft = Math.max(0, x(today) - 160))}
        >
          Today
        </button>
        <div className="tk-segmented tk-segmented-sm" role="group" aria-label="Show">
          <button type="button" className={scope === 'all' ? 'is-active' : ''} onClick={() => setScope('all')}>All topics</button>
          <button type="button" className={scope === 'scheduled' ? 'is-active' : ''} onClick={() => setScope('scheduled')}>Scheduled</button>
        </div>
        <FoldButtons fold={fold} />
        <label className="tp-gantt-start" title="Nothing is planned before this day">
          <span className="tk-muted">Project starts</span>
          <input
            type="date"
            className="tk-input"
            value={project.project_start || ''}
            onChange={(e) => updateList(project.id, { project_start: e.target.value || null })}
            disabled={readOnly}
          />
        </label>
        <span className="tp-gantt-legend" aria-hidden="true">
          <span className="tp-key tp-key-critical" /> Critical
          <span className="tp-key tp-key-late" /> Late
          <span className="tp-key tp-key-conflict" /> Conflict
          <span className="tp-key tp-key-deadline" /> Due date
          <span className="tp-key tp-key-event" /> Event
        </span>
      </div>

      {nothingPlanned && items.length > 0 && (
        <p className="tp-gantt-hint">
          <TaskIcon name="gantt" size={14} /> Nothing is scheduled yet. Open a topic's ⋯ › Schedule to give it a duration or dates, link
          tasks with "Waits for", or double-click a day on a topic's row.
        </p>
      )}

      <div ref={scrollRef} className="tp-gantt" style={{ '--tp-left': `${LEFT}px` } as React.CSSProperties}>
        <div className="tp-g-inner" style={{ width: LEFT + width }}>
          <div className="tp-g-head">
            <div className="tp-g-corner">Topic</div>
            <div className="tp-g-scale" style={{ width }}>
              <div className="tp-g-ticks tp-g-ticks-top">
                {ticks.top.map((t) => (
                  <span key={t.key} style={{ left: t.x, width: t.w }}>{t.label}</span>
                ))}
              </div>
              <div className="tp-g-ticks">
                {ticks.bottom.map((t) => (
                  <span key={t.key} className={t.key === today ? 'is-today' : ''} style={{ left: t.x, width: t.w }}>{t.label}</span>
                ))}
              </div>
            </div>
          </div>

          <div ref={bodyRef} className="tp-g-body" style={{ height: Math.max(rows.length, 1) * ROW }}>
            <div className="tp-g-grid" style={{ left: LEFT, width, backgroundImage: grid }}>
              {events.map((ev) => (
                <div
                  key={ev.id}
                  className="tp-g-band"
                  style={{ left: x(ev.plan_start!), width: (daysBetween(ev.plan_start!, ev.plan_finish!) + 1) * px }}
                  title={ev.text}
                />
              ))}
              <div className="tp-g-today" style={{ left: x(today) + px / 2 }} />
            </div>

            <svg className="tp-g-links" style={{ left: LEFT }} width={width} height={Math.max(rows.length, 1) * ROW} aria-hidden="true">
              <defs>
                <marker id="tp-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M0 0 L8 4 L0 8 z" className="tp-g-arrowhead" />
                </marker>
                <marker id="tp-arrow-critical" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M0 0 L8 4 L0 8 z" className="tp-g-arrowhead is-critical" />
                </marker>
                <marker id="tp-arrow-conflict" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M0 0 L8 4 L0 8 z" className="tp-g-arrowhead is-conflict" />
                </marker>
              </defs>
              {arrows.map((a) => (
                <path
                  key={a.key}
                  d={a.d}
                  className={`tp-g-arrow${a.conflict ? ' is-conflict' : a.critical ? ' is-critical' : ''}`}
                  markerEnd={`url(#${a.conflict ? 'tp-arrow-conflict' : a.critical ? 'tp-arrow-critical' : 'tp-arrow'})`}
                />
              ))}
              {dragLine && <path d={dragLine} className="tp-g-arrow is-drawing" markerEnd="url(#tp-arrow)" />}
            </svg>

            {rows.map((r) => (
              <GanttRow
                key={r.item.ckey || r.item.id}
                row={r}
                dates={datesOf(r.item)}
                from={from}
                px={px}
                readOnly={readOnly}
                selected={selected === r.item.id}
                flash={flashId === r.item.id}
                collapsed={collapsed.has(r.item.id)}
                linkTarget={drag?.mode === 'link' && drag.overId === r.item.id}
                onSelect={() => setSelected(r.item.id)}
                onToggle={() => toggle(r.item)}
                onAddChild={() => addChild(r.item)}
                onBarPointerDown={onBarPointerDown(r.item)}
                onScheduleAt={(day) => scheduleAt(r.item, day)}
                onTrackPointerDown={onTrackPointerDown(r.item)}
                range={drag?.mode === 'range' && drag.moved && drag.id === r.item.id ? rangeOf(drag) : null}
              />
            ))}
            {!rows.length && (
              <p className="tp-gantt-empty">{items.length ? 'Nothing scheduled to show.' : 'Nothing in this project yet — add topics on the map.'}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttView;
